import { ReactNode } from 'react'

import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  GreyGrey100,
  IconClipboard,
  Modal,
  Primary13Regular,
  Primary14SemiBold,
  useNotifications
} from '@sequencehq/core-components'
import { dateTimeWithFormat, toMoney } from '@sequencehq/utils'
import { Currency } from '@sequencehq/api'
import { CurrentPayment } from 'Invoices/types'

import { PaymentOrigin } from './PaymentOrigin'

/**
 * Read-only drawer showing a single recorded payment, styled to match the
 * schedule's price-detail drawer (ProductPriceMeta). Data comes from the
 * invoice's current payment — there is no dedicated payment endpoint yet.
 * Mounted while open, so it unmounts on close.
 */
export const PaymentDetailsDrawer = ({
  payment,
  currency,
  onClose
}: {
  payment: CurrentPayment
  currency: Currency
  onClose: () => void
}) => {
  const { displayNotification } = useNotifications()

  const copyPaymentId = () => {
    void navigator.clipboard.writeText(payment.paymentId)
    displayNotification('Payment ID copied', {
      type: 'success',
      isClosable: true,
      duration: 2000
    })
  }

  const copyPaymentReference = () => {
    if (!payment.reference) return

    void navigator.clipboard.writeText(payment.reference)
    displayNotification('Payment reference copied', {
      type: 'success',
      isClosable: true,
      duration: 2000
    })
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="drawer"
      width="550px"
      data-testid="payment-details.drawer"
    >
      <Modal.Header showCloseButton withBorder>
        Payment details
      </Modal.Header>
      <Modal.Content padding="24px">
        <Flex flexDirection="column" data-testid="payment-details.content">
          <Box {...Primary14SemiBold} color={GreyGrey100}>
            Details
          </Box>
          <Grid
            templateColumns="160px 1fr"
            gap="12px"
            mt="16px"
            color={GreyGrey70}
          >
            <DetailLabel>Amount</DetailLabel>
            <DetailValue>
              {toMoney({ value: payment.amount, currency })}
            </DetailValue>

            <DetailLabel>Currency</DetailLabel>
            <DetailValue>{currency}</DetailValue>

            <DetailLabel>Origin</DetailLabel>
            <PaymentOrigin
              origin={payment.origin}
              color={GreyGrey90}
              data-testid="payment-details.origin"
            />

            <DetailLabel>Payment date</DetailLabel>
            <DetailValue>
              {dateTimeWithFormat(payment.paidAt, 'MMM d, yyyy')}
            </DetailValue>

            {payment.reference && (
              <>
                <DetailLabel>Reference</DetailLabel>
                <CopyableIdentifier
                  value={payment.reference}
                  onCopy={copyPaymentReference}
                  label="payment reference"
                  data-testid="payment-details.reference"
                />
              </>
            )}

            <DetailLabel>Payment ID</DetailLabel>
            <CopyableIdentifier
              value={payment.paymentId}
              onCopy={copyPaymentId}
              label="payment ID"
              data-testid="payment-details.paymentId"
            />
          </Grid>
        </Flex>
      </Modal.Content>
    </Modal>
  )
}

const DetailLabel = ({ children }: { children: ReactNode }) => (
  <Box {...Primary13Regular}>{children}</Box>
)

const DetailValue = ({ children }: { children: ReactNode }) => (
  <Box {...Primary13Regular} color={GreyGrey90}>
    {children}
  </Box>
)

const CopyableIdentifier = ({
  value,
  onCopy,
  label,
  'data-testid': dataTestId
}: {
  value: string
  onCopy: () => void
  label: string
  'data-testid': string
}) => (
  <Flex gap="8px" alignItems="center" minWidth={0} height="16px">
    <Badge
      sentiment="neutral"
      containerStyle={{ height: '16px', padding: 1 }}
      data-testid={dataTestId}
      truncate
    >
      {value}
    </Badge>
    <Box flexShrink={0} display="flex">
      <Button
        variant="unstyled"
        size="24px"
        onClick={onCopy}
        aria-label={`Copy ${label}`}
        data-testid={`${dataTestId}.copy`}
      >
        <IconClipboard size={14} color={GreyGrey60} />
      </Button>
    </Box>
  </Flex>
)
